/**
 * @fileoverview カート／カートドロワーのフォーム submit 時に、購入上限（タグ）と商品タイプ別の最低数量を検証する。
 * `theme.liquid` の `window.pdsCartRules` と、`pds-cart-line-qty-attrs` が出力する数量 input の data 属性に依存する。
 */

/**
 * `theme.liquid` で定義する `window.pdsCartRules` の最小構造。
 * @typedef {object} PdsCartRules
 * @property {{ type: string, min: number }[]} [minByProductType]
 * @property {PdsCartRulesStrings} strings
 */

/**
 * @typedef {object} PdsCartRulesStrings
 * @property {string} lineOverTagMax - `[product]` と `[max]` を含む翻訳文字列
 * @property {string} [lineOverTagMaxFallbackProduct] - 商品名が取れないときの表示用
 * @property {string} minTypeRequired - `[type]` `[min]` `[current]`
 */

(function () {
  /**
   * @returns {PdsCartRules|undefined}
   */
  const getRules = () => window.pdsCartRules;

  /**
   * `data-pds-tag-max` 付きの行を `product_id` 単位で合算し、タグ上限を超えていればメッセージを返す。
   * 商品名は `data-pds-product-title`（複数行で最小の tagMax を採用する場合あり）。
   *
   * @param {HTMLFormElement} form - `#cart` または `#CartDrawer-Form`
   * @returns {string[]} 表示用エラーメッセージ（違反がなければ空）
   */
  function validateTagLimits(form) {
    const rules = getRules();
    const messages = [];
    if (!rules) return messages;

    /** @type {Map<string, { tagMax: number, sum: number, title: string }>} */
    const byProduct = new Map();

    form.querySelectorAll('.quantity__input[name="updates[]"]').forEach((input) => {
      const tagMax = parseInt(input.getAttribute('data-pds-tag-max') || '', 10);
      if (!(tagMax > 0)) return;
      const pid = input.getAttribute('data-pds-product-id');
      if (!pid) return;
      const q = parseInt(input.value, 10) || 0;
      const titleAttr = (input.getAttribute('data-pds-product-title') || '').trim();

      const prev = byProduct.get(pid);
      if (!prev) {
        byProduct.set(pid, { tagMax, sum: q, title: titleAttr });
      } else {
        prev.sum += q;
        if (tagMax < prev.tagMax) prev.tagMax = tagMax;
        if (!prev.title && titleAttr) prev.title = titleAttr;
      }
    });

    byProduct.forEach((row) => {
      if (row.sum > row.tagMax) {
        const productLabel =
          row.title ||
          rules.strings.lineOverTagMaxFallbackProduct ||
          '';
        messages.push(
          rules.strings.lineOverTagMax.replace('[product]', productLabel).replace('[max]', String(row.tagMax))
        );
      }
    });
    return messages;
  }

  /**
   * `[data-pds-product-type]` 行ごとに数量をタイプ別で合算する。
   * そのタイプが 1 個でもカートにあり、かつ `min` 未満ならエラー（タイプ同士の数量は足し合わせない）。
   *
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  function validateMinByProductType(form) {
    const rules = getRules();
    const messages = [];
    if (!rules || !rules.minByProductType || !rules.minByProductType.length) return messages;

    const totals = {};
    form.querySelectorAll('[data-pds-product-type]').forEach((row) => {
      const type = row.getAttribute('data-pds-product-type');
      if (!type) return;
      const input = row.querySelector('.quantity__input[name="updates[]"]');
      if (!input) return;
      const q = parseInt(input.value, 10) || 0;
      totals[type] = (totals[type] || 0) + q;
    });

    rules.minByProductType.forEach((rule) => {
      const type = rule.type && String(rule.type).trim();
      if (!type) return;
      const min = parseInt(rule.min, 10);
      if (!(min > 0)) return;
      const total = totals[type] || 0;
      if (total > 0 && total < min) {
        messages.push(
          rules.strings.minTypeRequired
            .replace('[type]', type)
            .replace('[min]', String(min))
            .replace('[current]', String(total))
        );
      }
    });
    return messages;
  }

  /**
   * 合計直下・チェックアウト直前の注意表示（`checkout_unavailable_notice` と同位置・同デザイン）。
   *
   * @param {HTMLFormElement} form
   * @returns {HTMLElement|null}
   */
  function getQuantityNoticeEl(form) {
    const formId = form.getAttribute('id');
    if (formId === 'CartDrawer-Form') {
      return document.getElementById('pds-cart-drawer-quantity-notice');
    }
    return document.getElementById('pds-cart-quantity-notice');
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {HTMLButtonElement|null}
   */
  function getCheckoutButton(form) {
    const formId = form.getAttribute('id');
    if (formId === 'CartDrawer-Form') {
      return document.getElementById('CartDrawer-Checkout');
    }
    return document.getElementById('checkout');
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} blockedByQuantityRules
   */
  function updateCheckoutButton(form, blockedByQuantityRules) {
    const btn = getCheckoutButton(form);
    if (!btn) return;
    // 添付品未選択ガード（pds-attached-gifts.js）と併用
    btn.disabled =
      blockedByQuantityRules ||
      btn.hasAttribute('data-pds-attachment-blocked') ||
      btn.hasAttribute('data-pds-checkout-locked');
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string[]} messages
   */
  function showCartErrors(form, messages) {
    const noticeEl = getQuantityNoticeEl(form);
    if (noticeEl) {
      noticeEl.textContent = messages.join(' ');
      noticeEl.hidden = messages.length === 0;
      if (messages.length > 0) noticeEl.focus?.();
    }
    updateCheckoutButton(form, messages.length > 0);
  }

  /**
   * @param {HTMLFormElement} form
   */
  function clearCartErrors(form) {
    const noticeEl = getQuantityNoticeEl(form);
    if (noticeEl) {
      noticeEl.textContent = '';
      noticeEl.hidden = true;
    }
    updateCheckoutButton(form, false);
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string[]}
   */
  function validateCartForm(form) {
    const tagMsgs = validateTagLimits(form);
    const minMsgs = validateMinByProductType(form);
    const messages = tagMsgs.concat(minMsgs);
    if (messages.length > 0) showCartErrors(form, messages);
    else clearCartErrors(form);
    return messages;
  }

  /**
   * @param {SubmitEvent} event
   */
  function onCartFormSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const messages = validateCartForm(form);

    if (messages.length > 0) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * `#cart` と `#CartDrawer-Form` に submit リスナーを一度だけ付与する。
   */
  function bindCartForms() {
    ['cart', 'CartDrawer-Form'].forEach((id) => {
      const form = document.getElementById(id);
      if (form && !form.dataset.pdsQuantityRulesBound) {
        form.dataset.pdsQuantityRulesBound = 'true';
        form.addEventListener('submit', onCartFormSubmit);
      }
    });
  }

  /**
   * フォームを送信せず、現在の入力値で検証しエラー領域を更新する。
   * カート Ajax 更新後（`cartUpdate`）の表示同期などで使用。
   *
   * @global
   * @function pdsValidateCartFormsForCheckout
   * @returns {void}
   */
  window.pdsValidateCartFormsForCheckout = function () {
    ['cart', 'CartDrawer-Form'].forEach((id) => {
      const form = document.getElementById(id);
      if (!form) return;
      validateCartForm(form);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    bindCartForms();
    window.pdsValidateCartFormsForCheckout();
  });

  document.addEventListener('shopify:section:load', bindCartForms);

  // テーマの pubsub（pubsub.js）: カートが Ajax で更新されたあと DOM が差し替わることがあるため、
  // フォームへ再度 bindCartForms で submit リスナーを付け直し、数量に合わせてエラー表示を更新する
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    const onCartUpdateForValidation =
      typeof debounce === 'function'
        ? debounce(() => {
            bindCartForms();
            window.pdsValidateCartFormsForCheckout();
          }, 200)
        : () => {
            bindCartForms();
            window.pdsValidateCartFormsForCheckout();
          };

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      onCartUpdateForValidation();
    });
  }
})();
